import { useSelector } from 'react-redux';
import findResult, { isEmpty } from '@/utils/resultsHelper';
import { readStates, saveStates } from '@/constants/lifecycle';
import { formatDate } from '@/utils/dateHelper';
import { newDatasourceSettings } from '../datasource/state/state';
import { resolveCatalogueTemplate, sectionsWithFields } from '../catalogue/helper';
import { datasetDirtyFlags, datasetSourceAndStepAttributes } from '../state/state';
import { datasetPerms } from '../sharing/state/state';
import { datasetSettingsFields } from '../settings/state/state';
import { canChangeDatasetFoundations } from '../helper';
import hasDatasourceChanged from '../datasource/helper';

const useDatasetCreatedOn = ({ category, code }) => useSelector(({ datasets: { datasets } }) => {
  const item = findResult(datasets, category, code) || { created_at: null };
  return { createdOn: isEmpty(item.updated_at) ? '' : formatDate(item.created_at) };
});

const useDatasetUpdatedOn = ({ category, code }) => useSelector(({ datasets: { datasets } }) => {
  const item = findResult(datasets, category, code) || { updated_at: null };
  return { updatedOn: isEmpty(item.updated_at) ? '' : formatDate(item.updated_at) };
});

const useDatasetWizardActionsContainer = ({ category, code }) => useSelector(({
  auth: { role: { permissions } },
  datasetDatasource: { settings },
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const datasourceSettings = findResult(settings, category, code) || newDatasourceSettings({ category, code });
  const { dataset, template, editable } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    catalogue: { title: '' },
    copy: { catalogue: { title: '' } },
    dictionaries: [],
    isDirty: datasetDirtyFlags(),
    permissions: datasetPerms(),
    steps: datasetSourceAndStepAttributes(),
    ...readStates,
    ...saveStates,
    settings: datasetSettingsFields(),
  });
  const {
    steps, isNew, readErrors, saveErrors, saveStatus, copy, catalogue,
  } = dataset;

  const { canChangeDatasource } = canChangeDatasetFoundations(dataset, permissions);
  const isDirty = {
    ...dataset.isDirty,
    datasource: hasDatasourceChanged(datasourceSettings, datasourceSettings.copy),
    template: !isEmpty(steps.templateCode) && (steps.templateCode !== dataset.settings.catalogue_definition_code),
  };

  if (!template.code) {
    template.code = dataset.settings.catalogue_definition_code;
    template.name = steps.templateName;
  }

  return {
    activeStepIndex: steps.activeStepIndex,
    canChangeDatasource,
    dataset,
    datasetCode: steps.code,
    datasourceSettings,
    disableEdit: !editable,
    isDirty,
    isNew,
    previousTitle: copy.catalogue?.title,
    readErrors: readErrors.concat(template.readErrors),
    saveErrors,
    saveStatus,
    sectionsWithFields: sectionsWithFields(template),
    template,
    title: catalogue.title,
  };
});

export default {
  useDatasetCreatedOn,
  useDatasetUpdatedOn,
  useDatasetWizardActionsContainer,
};
